import { TablePage, Dialog, TabView, View } from 'web-mojo';
import { Incident, IncidentList, IncidentForms, Ticket, TicketList, TicketForms } from 'web-mojo/admin-models';

export default class IncidentsPage extends TablePage {
    constructor(options = {}) {
        super({
            title: 'Incidents',
            icon: 'bi-exclamation-triangle-fill',
            pageName: 'incidents',
            route: 'group/incidents',
            permissions: ['manage_routes', 'manage_group'],
            requiresGroup: true,

            Collection: IncidentList,

            defaultQuery: {
                sort: '-created',
                size: 50,
            },

            columns: [
                {
                    label: 'Severity',
                    key: 'severity',
                    filter: {
                        type: 'select',
                        options: [
                            { label: 'Critical', value: 'critical' },
                            { label: 'High',     value: 'high' },
                            { label: 'Medium',   value: 'medium' },
                            { label: 'Low',      value: 'low' },
                        ]
                    }
                },
                {
                    label: 'Title',
                    key: 'title',
                    formatter: "default('—')",
                },
                {
                    label: 'Status',
                    key: 'status',
                    filter: {
                        type: 'select',
                        options: [
                            { label: 'Open',     value: 'open' },
                            { label: 'Resolved', value: 'resolved' },
                            { label: 'Ignored',  value: 'ignored' },
                        ]
                    }
                },
                {
                    label: 'Created',
                    key: 'created',
                    formatter: 'relative',
                    sortable: true,
                    visibility: 'md',
                },
                {
                    label: 'Updated',
                    key: 'updated',
                    formatter: 'relative',
                    sortable: true,
                    visibility: 'lg',
                },
            ],

            selectable: false,
            searchable: true,
            sortable: true,
            filterable: true,
            paginated: true,

            showRefresh: true,
            showAdd: true,
            addLabel: 'New Incident',
            showExport: false,

            tableOptions: {
                striped: false,
                bordered: false,
                hover: true,
                responsive: true,
            },

            contextMenu: [
                { label: 'View / Edit', action: 'view-incident' },
                { label: 'Add Ticket',  action: 'add-ticket' },
                { label: 'Resolve',     action: 'resolve-incident' },
                { label: 'Ignore',      action: 'ignore-incident', className: 'text-secondary' },
            ],

            ...options,
        });
    }

    // ── Toolbar ───────────────────────────────────────────────────────────────

    onActionAdd() {
        const form = new IncidentFormView();
        Dialog.showDialog({
            title: 'New Incident',
            body: form,
            size: 'md',
            buttons: [
                { text: 'Cancel', class: 'btn-secondary', dismiss: true },
                { text: 'Create Incident', class: 'btn-primary',
                  handler: async () => {
                      const ok = await form.onActionSave();
                      if (ok) this.collection.fetch();
                      return ok;
                  }
                },
            ],
        });
    }

    // ── Row actions ───────────────────────────────────────────────────────────

    onActionView(event, el) {
        this.onActionViewIncident(event, el);
    }

    onActionViewIncident(event, el) {
        const id = el.getAttribute('data-id');
        const incident = this.collection.get(id);
        if (!incident) return;

        const detail = new IncidentDetailView({ incident });
        Dialog.showDialog({
            title: incident.get('title') || `Incident #${id}`,
            body: detail,
            size: 'lg',
            footer: false,
        });
    }

    onActionAddTicket(event, el) {
        const id = el.getAttribute('data-id');
        const incident = this.collection.get(id);
        if (!incident) return;

        const form = new TicketFormView({ incidentId: incident.get('id') });
        Dialog.showDialog({
            title: 'Add Ticket',
            body: form,
            size: 'md',
            buttons: [
                { text: 'Cancel', class: 'btn-secondary', dismiss: true },
                { text: 'Create Ticket', class: 'btn-primary',
                  handler: () => form.onActionSave()
                },
            ],
        });
    }

    async onActionResolveIncident(event, el) {
        const id = el.getAttribute('data-id');
        const confirmed = await Dialog.confirm({
            title: 'Resolve Incident',
            message: 'Mark this incident as resolved?',
            confirmText: 'Resolve',
            confirmClass: 'btn-success',
        });
        if (!confirmed) return;

        const incident = this.collection.get(id);
        if (!incident) return;
        const response = await incident.save({ status: 'resolved' });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to resolve incident');
            return;
        }
        app.toast.success('Incident resolved');
        this.collection.fetch();
    }

    async onActionIgnoreIncident(event, el) {
        const id = el.getAttribute('data-id');
        const incident = this.collection.get(id);
        if (!incident) return;
        const response = await incident.save({ status: 'ignored' });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to ignore incident');
            return;
        }
        app.toast.success('Incident ignored');
        this.collection.fetch();
    }
}

// ── Incident detail with tabs (Events + Tickets) ──────────────────────────────

class IncidentDetailView extends View {
    constructor({ incident }) {
        super();
        this.incident = incident;
    }

    async onInit() {
        const id = this.incident.get('id');

        this.tabs = new TabView({
            containerId: 'incident-tabs',
            tabs: [
                { id: 'overview', label: 'Overview',  icon: 'bi-info-circle' },
                { id: 'tickets',  label: 'Tickets',   icon: 'bi-ticket-detailed' },
                { id: 'history',  label: 'History',   icon: 'bi-clock-history' },
            ],
        });
        this.addChild(this.tabs);

        this.overviewPanel = new IncidentOverviewPanel({ incident: this.incident });
        this.tabs.addContent('overview', this.overviewPanel);

        this.ticketsPanel = new IncidentTicketsPanel({ incidentId: id });
        this.tabs.addContent('tickets', this.ticketsPanel);

        this.historyPanel = new IncidentHistoryPanel({ incidentId: id });
        this.tabs.addContent('history', this.historyPanel);
    }

    get template() {
        return `<div data-container="incident-tabs"></div>`;
    }

    set template(v) { this._t = v; }
}

class IncidentOverviewPanel extends View {
    constructor({ incident }) {
        super();
        this.incident = incident;
    }

    get template() {
        const i = this.incident.attributes;
        const severityClass = {
            critical: 'danger', high: 'warning', medium: 'info', low: 'secondary'
        }[i.severity] || 'secondary';

        return `
            <div class="p-3">
                <div class="d-flex gap-2 mb-3 flex-wrap">
                    <span class="badge bg-${severityClass}">${i.severity || '—'}</span>
                    <span class="badge bg-secondary">${i.status || '—'}</span>
                    ${i.category ? `<span class="badge bg-light text-dark border">${i.category}</span>` : ''}
                </div>
                ${i.description ? `<p class="text-muted small">${i.description}</p>` : ''}
                <div class="row g-3 small">
                    <div class="col-6">
                        <div class="text-muted">Created</div>
                        <div>${i.created ? new Date(i.created).toLocaleString() : '—'}</div>
                    </div>
                    <div class="col-6">
                        <div class="text-muted">Updated</div>
                        <div>${i.updated ? new Date(i.updated).toLocaleString() : '—'}</div>
                    </div>
                    ${i.component ? `
                    <div class="col-6">
                        <div class="text-muted">Component</div>
                        <div>${i.component}</div>
                    </div>` : ''}
                    ${i.component_id ? `
                    <div class="col-6">
                        <div class="text-muted">Component ID</div>
                        <div>${i.component_id}</div>
                    </div>` : ''}
                </div>
            </div>
        `;
    }

    set template(v) { this._t = v; }
}

class IncidentTicketsPanel extends View {
    constructor({ incidentId }) {
        super();
        this.incidentId = incidentId;
        this.tickets = [];
        this.loading = false;
    }

    async onInit() {
        this.loading = true;
        this.render();
        const response = await app.api.get('/api/incident/ticket', { incident: this.incidentId, size: 50 });
        this.loading = false;
        if (response.success && response.data.status) {
            this.tickets = response.data.data || [];
        }
        this.render();
    }

    onActionAddTicket() {
        const form = new TicketFormView({ incidentId: this.incidentId });
        Dialog.showDialog({
            title: 'Add Ticket',
            body: form,
            size: 'md',
            buttons: [
                { text: 'Cancel', class: 'btn-secondary', dismiss: true },
                { text: 'Create Ticket', class: 'btn-primary',
                  handler: async () => {
                      const ok = await form.onActionSave();
                      if (ok) await this.onInit();
                      return ok;
                  }
                },
            ],
        });
    }

    get template() {
        if (this.loading) return `<div class="text-center py-4 text-muted"><div class="spinner-border spinner-border-sm me-2"></div>Loading…</div>`;

        const rows = this.tickets.map(t => {
            const statusClass = { open: 'primary', resolved: 'success', new: 'secondary', paused: 'warning' }[t.status] || 'secondary';
            return `
                <div class="d-flex align-items-start gap-2 py-2 border-bottom">
                    <span class="badge bg-${statusClass} mt-1">${t.status || '—'}</span>
                    <div class="flex-grow-1">
                        <div class="small fw-semibold">${t.title || '—'}</div>
                        ${t.description ? `<div class="text-muted small">${t.description}</div>` : ''}
                    </div>
                    <div class="text-muted small flex-shrink-0">${t.created ? new Date(t.created).toLocaleDateString() : ''}</div>
                </div>
            `;
        }).join('');

        return `
            <div class="p-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <span class="small text-muted">${this.tickets.length} ticket${this.tickets.length !== 1 ? 's' : ''}</span>
                    <button type="button" class="btn btn-sm btn-outline-primary" data-action="add-ticket">
                        <i class="bi bi-plus-lg me-1"></i>Add Ticket
                    </button>
                </div>
                ${this.tickets.length > 0 ? `<div class="border rounded">${rows}</div>` : '<p class="text-muted small text-center py-3">No tickets yet.</p>'}
            </div>
        `;
    }

    set template(v) { this._t = v; }
}

class IncidentHistoryPanel extends View {
    constructor({ incidentId }) {
        super();
        this.incidentId = incidentId;
        this.events = [];
        this.loading = false;
    }

    async onInit() {
        this.loading = true;
        this.render();
        const response = await app.api.get('/api/incident/event', { incident: this.incidentId, size: 100 });
        this.loading = false;
        if (response.success && response.data.status) {
            this.events = response.data.data || [];
        }
        this.render();
    }

    get template() {
        if (this.loading) return `<div class="text-center py-4 text-muted"><div class="spinner-border spinner-border-sm me-2"></div>Loading…</div>`;

        if (this.events.length === 0) return `<p class="text-muted small text-center py-4">No history.</p>`;

        const rows = this.events.map(e => `
            <div class="d-flex gap-2 py-2 border-bottom small">
                <div class="text-muted flex-shrink-0" style="min-width:120px;">${e.created ? new Date(e.created).toLocaleString() : ''}</div>
                <div class="flex-grow-1">
                    <span class="fw-semibold">${e.category || e.description || '—'}</span>
                    ${e.details ? `<div class="text-muted">${e.details}</div>` : ''}
                </div>
            </div>
        `).join('');

        return `<div class="p-3"><div class="border rounded">${rows}</div></div>`;
    }

    set template(v) { this._t = v; }
}

// ── Forms ─────────────────────────────────────────────────────────────────────

class IncidentFormView extends View {
    async onActionSave() {
        const title = this.element.querySelector('[name=title]')?.value.trim();
        const description = this.element.querySelector('[name=description]')?.value.trim();
        const severity = this.element.querySelector('[name=severity]')?.value;

        if (!title) { app.toast.warning('Title is required'); return false; }

        const response = await app.api.post('/api/incident/incident', {
            title,
            description: description || null,
            severity: severity || 'medium',
            status: 'open',
            group: app.groupId(),
        });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to create incident');
            return false;
        }
        app.toast.success('Incident created');
        this.emit('saved', response.data.data);
        return true;
    }

    get template() {
        return `
            <div class="p-3">
                <div class="mb-3">
                    <label class="form-label">Title <span class="text-danger">*</span></label>
                    <input type="text" class="form-control" name="title" placeholder="Brief description of the incident">
                </div>
                <div class="mb-3">
                    <label class="form-label">Severity</label>
                    <select class="form-select" name="severity">
                        <option value="low">Low</option>
                        <option value="medium" selected>Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                    </select>
                </div>
                <div class="mb-4">
                    <label class="form-label">Description</label>
                    <textarea class="form-control" name="description" rows="3" placeholder="Additional details…"></textarea>
                </div>
            </div>
        `;
    }

    set template(v) { this._t = v; }
}

class TicketFormView extends View {
    constructor({ incidentId = null } = {}) {
        super();
        this.incidentId = incidentId;
    }

    async onActionSave() {
        const title = this.element.querySelector('[name=title]')?.value.trim();
        const description = this.element.querySelector('[name=description]')?.value.trim();
        const priority = this.element.querySelector('[name=priority]')?.value;

        if (!title) { app.toast.warning('Title is required'); return false; }

        const payload = {
            title,
            description: description || null,
            priority: parseInt(priority) || 5,
            status: 'new',
            category: 'incident',
        };
        if (this.incidentId) payload.incident = this.incidentId;

        const response = await app.api.post('/api/incident/ticket', payload);
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to create ticket');
            return false;
        }
        app.toast.success('Ticket created');
        this.emit('saved', response.data.data);
        return true;
    }

    get template() {
        return `
            <div class="p-3">
                <div class="mb-3">
                    <label class="form-label">Title <span class="text-danger">*</span></label>
                    <input type="text" class="form-control" name="title" placeholder="Ticket title">
                </div>
                <div class="mb-3">
                    <label class="form-label">Priority <span class="text-muted small">(1 = highest)</span></label>
                    <input type="number" class="form-control" name="priority" value="5" min="1" max="10">
                </div>
                <div class="mb-4">
                    <label class="form-label">Description</label>
                    <textarea class="form-control" name="description" rows="3"></textarea>
                </div>
            </div>
        `;
    }

    set template(v) { this._t = v; }
}
